import React, { useState, useEffect } from 'react';
import {
  Card,
  Button,
  Space,
  Row,
  Col,
  Form,
  InputNumber,
  message,
  Divider,
  Typography,
  Alert,
  Select,
  Switch
} from 'antd';
import {
  SettingOutlined,
  CheckCircleOutlined
} from '@ant-design/icons';
import styled from 'styled-components';
import { gamificationApi } from '../../../shared/api/gamificationApi';
import { GamificationConfig } from './types';

const { Text, Title } = Typography;
const { Option } = Select;

const StyledCard = styled(Card)`
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

export const ConfigTab: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      setInitialLoading(true);
      const data = await gamificationApi.getConfig();
      form.setFieldsValue(data);
    } catch (error: any) {
      if (error.response?.status === 404) {
        // No config exists yet, use defaults
        message.info('No hay configuración previa, usando valores por defecto');
      } else {
        message.error('Error al cargar configuración');
        console.error('[ConfigTab] ❌ Error al cargar:', error);
      }
    } finally {
      setInitialLoading(false);
    }
  };

  const onFinish = async (values: GamificationConfig) => {
    try {
      setLoading(true);
      await gamificationApi.updateConfig(values);
      message.success('Configuración guardada exitosamente');
      await loadConfig();
    } catch (error) {
      message.error('Error al guardar configuración');
      console.error('[ConfigTab] ❌ Error al guardar:', error);
    } finally {
      setLoading(false);
    }
  };

  // Guardar automáticamente al cambiar los switches principales
  const handleSwitchChange = async (field: 'gamificationEnabled' | 'messagesEnabled', value: boolean) => {
    try {
      // Actualizar el valor del switch en el formulario
      form.setFieldsValue({ [field]: value });

      // Guardar en el backend



      message.success(
        value
          ? `${field === 'gamificationEnabled' ? 'Sistema de gamificación' : 'Mensajes'} activado`
          : `${field === 'gamificationEnabled' ? 'Sistema de gamificación' : 'Mensajes'} desactivado`
      );
    } catch (error) {
      message.error('Error al guardar');
      console.error('[ConfigTab] ❌ Error:', error);
      // Revertir el cambio en el formulario si falla
      form.setFieldsValue({ [field]: !value });
    }
  };

  if (initialLoading) {
    return <Card loading />;
  }

  return (
    <StyledCard title={
      <Space>
        <SettingOutlined style={{ color: '#ac4949' }} />
        <span>Configuración del Sistema de Gamificación</span>
      </Space>
    }>
      <Alert
        message="Sistema de Gamificación"
        description="Configura cómo funcionará el sistema de bonus automáticos para tus usuarios. Los cambios se aplicarán inmediatamente."
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
      />

      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{
          gamificationEnabled: true,
          messagesEnabled: true,
          streakBreakHours: 24,
          dayResetHour: 0,
          dayResetTimezone: 'America/Argentina/Buenos_Aires',
          levelsEnabled: true,
          autoLevelUp: true,
          showBonusInMessage: true,
          showProgressInMessage: true,
          showNextObjective: true,
          rolloverEnabled: false,
          rolloverCronEnabled: false,
        }}
      >
        <Title level={5}>🎮 Activación General</Title>
        <Alert
          message="Control Principal del Sistema"
          description="Estas opciones controlan la activación completa del sistema de gamificación y sus mensajes."
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Sistema de Gamificación"
              name="gamificationEnabled"
              tooltip="Activa o desactiva completamente el sistema de bonus automáticos. Si está desactivado, NO se aplicarán bonus de gamificación."
              valuePropName="checked"
            >
              <Switch
                checkedChildren="Activado"
                unCheckedChildren="Desactivado"
                onChange={(checked) => handleSwitchChange('gamificationEnabled', checked)}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Mensajes de Bonificación"
              name="messagesEnabled"
              tooltip="Activa o desactiva los mensajes personalizados. Si está desactivado, se mostrará 'Configuración de mensajes desactivada'."
              valuePropName="checked"
            >
              <Switch
                checkedChildren="Activado"
                unCheckedChildren="Desactivado"
                onChange={(checked) => handleSwitchChange('messagesEnabled', checked)}
              />
            </Form.Item>
          </Col>
        </Row>

        <Divider />

        <Title level={5}>⏰ Configuración de Tiempo</Title>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Horas para romper racha"
              name="streakBreakHours"
              tooltip="Número de horas sin realizar una carga para que la racha se reinicie"
              rules={[{ required: true, message: 'Campo requerido' }]}
            >
              <InputNumber
                min={1}
                max={72}
                style={{ width: '100%' }}
                addonAfter="horas"
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              label="Hora de reset del día"
              name="dayResetHour"
              tooltip="Hora en la que se resetean los contadores diarios (formato 24h)"
              rules={[{ required: true, message: 'Campo requerido' }]}
            >
              <InputNumber
                min={0}
                max={23}
                style={{ width: '100%' }}
                addonAfter="hs"
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          label="Zona Horaria"
          name="dayResetTimezone"
          tooltip="Zona horaria para cálculos de días y rachas"
          rules={[{ required: true, message: 'Campo requerido' }]}
        >
          <Select>
            <Option value="America/Argentina/Buenos_Aires">🇦🇷 Argentina (GMT-3)</Option>
            <Option value="America/Montevideo">🇺🇾 Uruguay (GMT-3)</Option>
            <Option value="America/Santiago">🇨🇱 Chile (GMT-3)</Option>
            <Option value="America/Sao_Paulo">🇧🇷 Brasil (GMT-3)</Option>
            <Option value="America/Mexico_City">🇲🇽 México (GMT-6)</Option>
            <Option value="America/Bogota">🇨🇴 Colombia (GMT-5)</Option>
            <Option value="UTC">🌍 UTC (GMT+0)</Option>
          </Select>
        </Form.Item>

        <Divider />

        <Title level={5}>🎯 Límites Globales (Opcional)</Title>
        <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
          Establece límites máximos para controlar el bonus total otorgado
        </Text>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Bonus máximo por carga"
              name="maxBonusPerLoad"
              tooltip="Límite de bonus que se puede otorgar en una sola operación"
            >
              <InputNumber
                min={0}
                style={{ width: '100%' }}
                placeholder="Sin límite"
                prefix="$"
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              label="Bonus máximo diario por usuario"
              name="maxDailyBonus"
              tooltip="Límite de bonus total que un usuario puede recibir por día"
            >
              <InputNumber
                min={0}
                style={{ width: '100%' }}
                placeholder="Sin límite"
                prefix="$"
              />
            </Form.Item>
          </Col>
        </Row>

        <Divider />

        <Title level={5}>Rollover y Anti-Lavado</Title>
        <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
          Controla las condiciones de rollover para usuarios que reciben bonus. El usuario debe apostar el monto requerido antes de retirar.
        </Text>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Sistema de Rollover"
              name="rolloverEnabled"
              tooltip="Activa el sistema de rollover para controlar retiros de usuarios con bonus activo"
              valuePropName="checked"
            >
              <Switch
                checkedChildren="Activado"
                unCheckedChildren="Desactivado"
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Cron balance $0"
              name="rolloverCronEnabled"
              tooltip="Cron diario a las 04:00 que cancela rollovers de usuarios con balance $0"
              valuePropName="checked"
            >
              <Switch
                checkedChildren="Activado"
                unCheckedChildren="Desactivado"
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Camino B Automatico"
              name="caminoBAutoEnabled"
              tooltip="Si esta activo, los retiros con rollover pendiente se aprueban automaticamente descontando el bonus. Si esta inactivo, se rechazan con el mensaje de rechazo estandar."
              valuePropName="checked"
            >
              <Switch
                checkedChildren="Activado"
                unCheckedChildren="Desactivado"
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              label="Multiplicador rollover default"
              name="defaultRolloverMultiplier"
              tooltip="Multiplicador de rollover por defecto si la regla no tiene uno. Ej: 20 = el usuario debe apostar 20x el bonus"
            >
              <InputNumber
                min={1}
                max={100}
                style={{ width: '100%' }}
                placeholder="20"
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="Multiplicador anti-lavado"
              name="antiLavadoMultiplier"
              tooltip="Multiplicador anti-lavado sobre el deposito. Ej: 3 = debe apostar 3x el deposito"
            >
              <InputNumber
                min={1}
                max={50}
                style={{ width: '100%' }}
                placeholder="3"
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="Deposito minimo para rollover"
              name="minDepositForRollover"
              tooltip="Monto minimo de deposito para que se aplique rollover en reglas"
            >
              <InputNumber
                min={0}
                style={{ width: '100%' }}
                placeholder="1000"
                prefix="$"
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              label="Tope apilamiento %"
              name="maxBonusCapPercentage"
              tooltip="Porcentaje maximo del deposito como bonus al apilar reglas"
            >
              <InputNumber
                min={1}
                max={100}
                style={{ width: '100%' }}
                placeholder="20"
                addonAfter="%"
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="Tope bonus usuario nuevo"
              name="maxBonusCapNewUser"
              tooltip="Monto maximo de bonus para usuarios nuevos (FIRST_LOAD_EVER)"
            >
              <InputNumber
                min={0}
                style={{ width: '100%' }}
                placeholder="10000"
                prefix="$"
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="Tope bonus regular"
              name="maxBonusCapRegular"
              tooltip="Monto maximo de bonus para usuarios regulares"
            >
              <InputNumber
                min={0}
                style={{ width: '100%' }}
                placeholder="5000"
                prefix="$"
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Bonus nivel con rollover activo"
              name="levelBonusWithActiveRollover"
              tooltip="Que hacer con el bonus de nivel si el usuario tiene rollover activo"
            >
              <Select placeholder="Seleccionar">
                <Option value="none">No dar bonus de nivel</Option>
                <Option value="add_to_existing">Sumar al rollover existente</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Divider />

        <Form.Item style={{ marginBottom: 0 }}>
          <Space>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              size="large"
              icon={<CheckCircleOutlined />}
            >
              Guardar Configuración
            </Button>
            <Button
              onClick={() => form.resetFields()}
              size="large"
            >
              Resetear
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </StyledCard>
  );
};

// ==================== LEVELS TAB ====================
